/**
 * Always-on side rail: PPW / EPC / Y1 monthly for Enfin + LightReach.
 * Pricing comes from the contract API (`PPWDetails`) and falls back to the bundled
 * Excel tables — the active source is always flagged in the header.
 * Partners view: rate select + escalator chips + one card per finance partner.
 * Grid view: full rate × escalator matrix for a single partner.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CurrencyDollar, Database, Info, Sparkle, WarningCircle, X } from '@phosphor-icons/react';
import type { PreContractIntakeForm } from '../lib/preContractTypes';
import {
    buildPartnerPpwMatrix,
    buildPreContractLivePpwQuotes,
    formatEscalatorPercent,
    formatPpaRate,
    sortPartnerQuotesForDisplay,
    type PreContractPpwMetricsOverride,
    type PpwMatrixCell,
    type PpwPartnerId,
    type PpwPartnerQuote,
} from '../lib/preContractPpwPricing';
import {
    applyPpwDetailsToLiveQuotes,
    buildPpwDetailsMatrix,
    describePpwDetailsFallback,
    fetchPpwDetails,
    type PpwDetailsFetchState,
} from '../lib/preContractPpwDetails';
import { resolveStoreOpportunityId } from '../lib/preContractTitle';
import { useOneButtonStore, type OneButtonState } from '../../store/oneButtonStore';
import '../styles/pre-contract-live-pricing.css';

export type PreContractLivePricingRailProps = {
    form: PreContractIntakeForm;
    /** When set, renders as side-panel content with a close control. */
    onClose?: () => void;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    /** Energy community sites should recommend Enfin without hiding other partners. */
    preferEnfinPartner?: boolean;
    /** Overrides the store opportunity id used for the PPWDetails request. */
    opportunityId?: string | number | null;
};

/** `target` uses form.targetMinPpaRate; otherwise a numeric table rate. */
type RateSelection = 'target' | number;
type PricingView = 'partners' | 'grid';

const GRID_PARTNERS: PpwPartnerId[] = ['LightReach', 'Enfin'];

const PARTNER_MARKS: Record<PpwPartnerId, string> = {
    LightReach: 'LR',
    Enfin: 'EN',
};

function partnerModifier(partner: PpwPartnerId): string {
    return partner === 'LightReach' ? 'lightreach' : 'enfin';
}

function formatMoney(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    });
}

function formatPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function nearlyEqual(a: number, b: number, eps = 1e-6): boolean {
    return Math.abs(a - b) <= eps;
}

function matrixCellKey(cell: Pick<PpwMatrixCell, 'rate' | 'escalator'>): string {
    return `${cell.rate}-${cell.escalator}`;
}

function PartnerCard({
    quote,
    recommended,
    bestValue,
}: {
    quote: PpwPartnerQuote;
    recommended?: boolean;
    bestValue?: boolean;
}) {
    return (
        <article
            className={`pci-lpr__card pci-lpr__card--${partnerModifier(quote.partner)}${
                quote.available ? '' : ' pci-lpr__card--muted'
            }${recommended ? ' pci-lpr__card--recommended' : ''}`}
        >
            <header className="pci-lpr__card-head">
                <span className="pci-lpr__card-mark" aria-hidden>
                    {PARTNER_MARKS[quote.partner]}
                </span>
                <div className="pci-lpr__card-id">
                    <h3 className="pci-lpr__card-name">{quote.partner}</h3>
                    <p className="pci-lpr__card-sub">
                        {quote.available
                            ? `${formatPpaRate(quote.rate)} · ${formatEscalatorPercent(quote.escalator)} escalator`
                            : 'No pricing for this input'}
                    </p>
                </div>
                <div className="pci-lpr__chips">
                    {recommended ? (
                        <span className="pci-lpr__chip pci-lpr__chip--recommended">
                            Recommended
                        </span>
                    ) : null}
                    {bestValue ? (
                        <span className="pci-lpr__chip pci-lpr__chip--best">Best PPW</span>
                    ) : null}
                    {quote.interpolated ? (
                        <span className="pci-lpr__chip">Interpolated</span>
                    ) : null}
                </div>
            </header>

            {quote.available ? (
                <div className="pci-lpr__card-body">
                    <div className="pci-lpr__card-hero">
                        <span>PPW</span>
                        <strong>{formatPpw(quote.ppw)}</strong>
                    </div>
                    <dl className="pci-lpr__card-metrics">
                        <div>
                            <dt>EPC</dt>
                            <dd>{formatMoney(quote.epc, 0)}</dd>
                        </div>
                        <div>
                            <dt>Y1 monthly</dt>
                            <dd>{formatMoney(quote.estimatedMonthly, 0)}</dd>
                        </div>
                    </dl>
                </div>
            ) : (
                <p className="pci-lpr__note pci-lpr__note--warn" role="status">
                    <WarningCircle size={14} weight="fill" aria-hidden />
                    {quote.reason || 'Unavailable'}
                </p>
            )}
        </article>
    );
}

export const PreContractLivePricingRail: React.FC<PreContractLivePricingRailProps> = ({
    form,
    onClose,
    metricsOverride = null,
    preferEnfinPartner = false,
    opportunityId: opportunityIdProp = null,
}) => {
    const [view, setView] = useState<PricingView>('partners');
    const [gridPartner, setGridPartner] = useState<PpwPartnerId>('LightReach');
    const gridPartnerDefaultedRef = useRef(false);
    const [escalator, setEscalator] = useState(0.0299);
    const [rateSelection, setRateSelection] = useState<RateSelection>('target');
    const [ppwState, setPpwState] = useState<PpwDetailsFetchState>({ status: 'loading' });

    const storeOpportunityId = useOneButtonStore((s: OneButtonState) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s: OneButtonState) => s.opportunityDetails);
    const opportunityId = useMemo(
        () => opportunityIdProp ?? resolveStoreOpportunityId() ?? storeOpportunityId ?? null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [opportunityIdProp, storeOpportunityId, opportunityDetails],
    );

    /** PPWDetails wants the real state code, not the NY/NYC pricing split. */
    const requestStateCode = String(form.installState || '').trim().toUpperCase();
    const utilityId = form.utilityCompanyId;

    useEffect(() => {
        let cancelled = false;
        setPpwState({ status: 'loading' });
        void fetchPpwDetails({
            opportunityId,
            stateCode: requestStateCode,
            utilityId,
        }).then((next) => {
            if (cancelled) return;
            const request = {
                opportunityId,
                stateCode: requestStateCode,
                utilityId: utilityId || null,
            };
            if (next.status === 'ready') {
                console.log('PPWDetails', {
                    request,
                    rows: next.dataset.rows.length,
                    byPartner: {
                        LightReach: next.dataset.byPartner.LightReach.length,
                        Enfin: next.dataset.byPartner.Enfin.length,
                    },
                    unmappedPartners: next.dataset.unmappedPartners,
                    sample: next.dataset.rows.slice(0, 5),
                });
            } else {
                console.log('PPWDetails fallback', {
                    request,
                    reason: next.reason,
                    detail: describePpwDetailsFallback(next),
                });
            }
            setPpwState(next);
        });
        return () => {
            cancelled = true;
        };
    }, [opportunityId, requestStateCode, utilityId]);

    const apiDataset = ppwState.status === 'ready' ? ppwState.dataset : null;

    const rateOverride = rateSelection === 'target' ? null : rateSelection;
    const result = useMemo(() => {
        const base = buildPreContractLivePpwQuotes(form, escalator, rateOverride, metricsOverride);
        if (!apiDataset) return base;
        return applyPpwDetailsToLiveQuotes(base, apiDataset, { escalator, rateOverride });
    }, [form, escalator, rateOverride, metricsOverride, apiDataset]);
    const featuredRate = result.targetPpaRate > 0 ? result.targetPpaRate : result.allInRate;

    const matrix = useMemo(() => {
        if (view !== 'grid') return null;
        if (apiDataset) {
            return buildPpwDetailsMatrix({
                partner: gridPartner,
                rows: apiDataset.byPartner[gridPartner],
                multiplier: result.multiplier,
                systemWatts: result.systemWatts,
                annualProductionKwh: result.annualProductionKwh,
                extraRate: featuredRate > 0 ? featuredRate : null,
            });
        }
        if (!result.state) return null;
        return buildPartnerPpwMatrix({
            partner: gridPartner,
            state: result.state,
            multiplier: result.multiplier,
            systemWatts: result.systemWatts,
            annualProductionKwh: result.annualProductionKwh,
            extraRate: featuredRate > 0 ? featuredRate : null,
        });
    }, [view, gridPartner, result, apiDataset, featuredRate]);

    const displayQuotes = useMemo(
        () => sortPartnerQuotesForDisplay(result.quotes, { preferEnfin: preferEnfinPartner }),
        [result.quotes, preferEnfinPartner],
    );

    /** Cheapest PPW among available partners — only meaningful when more than one quotes. */
    const bestPpwPartner = useMemo<PpwPartnerId | null>(() => {
        const priced = displayQuotes.filter((quote) => quote.available && quote.ppw != null);
        if (priced.length < 2) return null;
        let best = priced[0];
        for (const quote of priced) {
            if ((quote.ppw ?? Infinity) < (best.ppw ?? Infinity)) best = quote;
        }
        return best.partner;
    }, [displayQuotes]);

    /** Cheapest PPW cell in the matrix, highlighted as the best economics. */
    const bestMatrixKey = useMemo<string | null>(() => {
        if (!matrix) return null;
        let best: PpwMatrixCell | null = null;
        for (const row of matrix.cells) {
            for (const cell of row) {
                if (!cell.available || cell.ppw == null) continue;
                if (best == null || best.ppw == null || cell.ppw < best.ppw) best = cell;
            }
        }
        return best ? matrixCellKey(best) : null;
    }, [matrix]);

    useEffect(() => {
        if (preferEnfinPartner && !gridPartnerDefaultedRef.current) {
            setGridPartner('Enfin');
            gridPartnerDefaultedRef.current = true;
        }
    }, [preferEnfinPartner]);

    useEffect(() => {
        setRateSelection('target');
    }, [form.targetMinPpaRate, form.installState]);

    useEffect(() => {
        if (!result.availableEscalators.length) return;
        if (!result.availableEscalators.some((e) => nearlyEqual(e, escalator, 1e-4))) {
            const preferred = result.availableEscalators.find((e) => nearlyEqual(e, 0.0299, 1e-4));
            setEscalator(preferred ?? result.availableEscalators[result.availableEscalators.length - 1]);
        }
    }, [result.availableEscalators, escalator]);

    const rateSelectValue = rateSelection === 'target' ? 'target' : String(rateSelection);
    const noTables = result.availableEscalators.length === 0;

    const isLivePricing = result.source === 'contract-api';
    const pricingLoading = ppwState.status === 'loading';
    const fallbackReason = ppwState.status === 'fallback' ? describePpwDetailsFallback(ppwState) : null;
    const sourceLabel = pricingLoading
        ? 'Checking live pricing'
        : isLivePricing
          ? 'Live · Contract API'
          : 'Excel fallback';
    const noPricingMessage = isLivePricing
        ? 'PPWDetails returned no rate rows for this partner yet.'
        : 'No Excel pricing tables for this state yet.';

    const selectCell = (cell: PpwMatrixCell) => {
        if (!cell.available) return;
        setEscalator(cell.escalator);
        if (featuredRate > 0 && nearlyEqual(cell.rate, featuredRate)) {
            setRateSelection('target');
        } else {
            setRateSelection(cell.rate);
        }
    };

    const handleRateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        if (value === 'target') {
            setRateSelection('target');
            return;
        }
        const next = Number(value);
        if (Number.isFinite(next) && next > 0) setRateSelection(next);
    };

    return (
        <section
            className={`pci-live-pricing-rail pci-lpr${
                onClose ? ' pci-live-pricing-rail--panel' : ''
            }${view === 'grid' ? ' pci-live-pricing-rail--grid' : ''}`}
            aria-label="Live finance partner pricing"
        >
            {/* Hero: brand + view switch */}
            <header className="pci-lpr__hero">
                <div className="pci-lpr__hero-top">
                    <div className="pci-lpr__brand">
                        <span className="pci-lpr__brand-icon" aria-hidden>
                            <CurrencyDollar size={18} weight="bold" />
                        </span>
                        <div>
                            <p className="pci-lpr__eyebrow">Finance partner pricing</p>
                            <h2 className="pci-lpr__title">Live pricing</h2>
                        </div>
                    </div>
                    <span
                        className={`pci-lpr__source${
                            isLivePricing ? ' pci-lpr__source--live' : ' pci-lpr__source--fallback'
                        }${pricingLoading ? ' pci-lpr__source--loading' : ''}`}
                        title={fallbackReason ?? undefined}
                        role="status"
                    >
                        {isLivePricing ? (
                            <Database size={12} weight="fill" aria-hidden />
                        ) : (
                            <WarningCircle size={12} weight="fill" aria-hidden />
                        )}
                        {sourceLabel}
                    </span>
                    {onClose ? (
                        <button
                            type="button"
                            className="pci-lpr__close"
                            onClick={onClose}
                            aria-label="Close pricing"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    ) : null}
                </div>

                <div className="pci-lpr__segment" role="tablist" aria-label="Pricing view">
                    <button
                        type="button"
                        role="tab"
                        className={`pci-lpr__segment-btn${
                            view === 'partners' ? ' pci-lpr__segment-btn--on' : ''
                        }`}
                        aria-selected={view === 'partners'}
                        onClick={() => setView('partners')}
                    >
                        Partners
                    </button>
                    <button
                        type="button"
                        role="tab"
                        className={`pci-lpr__segment-btn${
                            view === 'grid' ? ' pci-lpr__segment-btn--on' : ''
                        }`}
                        aria-selected={view === 'grid'}
                        onClick={() => setView('grid')}
                    >
                        Rate grid
                    </button>
                </div>
            </header>

            <div className="pci-lpr__body">
                {/* Design inputs feeding every quote below */}
                <dl className="pci-lpr__stats" aria-label="Design pricing inputs">
                    <div className="pci-lpr__stat pci-lpr__stat--state">
                        <dt>State</dt>
                        <dd>{result.state || '—'}</dd>
                    </div>
                    <div className="pci-lpr__stat pci-lpr__stat--rate">
                        <dt>Target/Min PPA</dt>
                        <dd>{featuredRate > 0 ? formatPpaRate(featuredRate) : '—'}</dd>
                    </div>
                    <div className="pci-lpr__stat pci-lpr__stat--size">
                        <dt>Size</dt>
                        <dd>{result.systemKw > 0 ? `${result.systemKw.toFixed(2)} kW` : '—'}</dd>
                    </div>
                    <div className="pci-lpr__stat pci-lpr__stat--prod">
                        <dt>Production</dt>
                        <dd>
                            {result.annualProductionKwh > 0
                                ? `${Math.round(result.annualProductionKwh).toLocaleString()} kWh`
                                : '—'}
                        </dd>
                    </div>
                    <div className="pci-lpr__stat pci-lpr__stat--mult">
                        <dt>Multiplier</dt>
                        <dd>{result.multiplier > 0 ? result.multiplier.toFixed(3) : '—'}</dd>
                    </div>
                </dl>

                {fallbackReason ? (
                    <p className="pci-lpr__note pci-lpr__note--warn" role="status">
                        <WarningCircle size={15} weight="fill" aria-hidden />
                        <span>
                            Showing Excel fallback pricing — not live partner data. {fallbackReason}
                        </span>
                    </p>
                ) : null}

                {apiDataset?.unmappedPartners.length ? (
                    <p className="pci-lpr__note" role="note">
                        <Info size={15} weight="fill" aria-hidden />
                        <span>
                            PPWDetails also returned {apiDataset.unmappedPartners.join(', ')}, which
                            the rail has no card for yet.
                        </span>
                    </p>
                ) : null}

                {view === 'partners' ? (
                    <>
                        {!noTables ? (
                            <div className="pci-lpr__controls">
                                <div>
                                    <span className="pci-lpr__control-label" id="pci-lpr-rate-label">
                                        PPA rate
                                    </span>
                                    <select
                                        className="pci-lpr__rate-select"
                                        value={rateSelectValue}
                                        aria-labelledby="pci-lpr-rate-label"
                                        onChange={handleRateChange}
                                    >
                                        <option value="target">
                                            {featuredRate > 0
                                                ? `Target/Min ${formatPpaRate(featuredRate)}`
                                                : 'Target/Min PPA rate'}
                                        </option>
                                        {result.availableRates.map((rate) => (
                                            <option key={rate} value={String(rate)}>
                                                {formatPpaRate(rate)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <span className="pci-lpr__control-label" id="pci-lpr-esc-label">
                                        Escalator
                                    </span>
                                    <div
                                        className="pci-lpr__esc-track"
                                        role="group"
                                        aria-labelledby="pci-lpr-esc-label"
                                    >
                                        {result.availableEscalators.map((value) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`pci-lpr__esc-btn${
                                                    nearlyEqual(value, result.escalator, 1e-4)
                                                        ? ' pci-lpr__esc-btn--on'
                                                        : ''
                                                }`}
                                                aria-pressed={nearlyEqual(
                                                    value,
                                                    result.escalator,
                                                    1e-4,
                                                )}
                                                onClick={() => setEscalator(value)}
                                            >
                                                {formatEscalatorPercent(value)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="pci-lpr__note pci-lpr__note--warn" role="status">
                                <WarningCircle size={15} weight="fill" aria-hidden />
                                {noPricingMessage}
                            </p>
                        )}

                        {preferEnfinPartner ? (
                            <p className="pci-lpr__note pci-lpr__note--good" role="status">
                                <Sparkle size={15} weight="fill" aria-hidden />
                                Enfin is recommended for energy community properties. Other partners
                                remain available.
                            </p>
                        ) : null}

                        <div className="pci-lpr__cards">
                            {displayQuotes.map((quote) => (
                                <PartnerCard
                                    key={quote.partner}
                                    quote={quote}
                                    recommended={
                                        preferEnfinPartner &&
                                        quote.partner === 'Enfin' &&
                                        quote.available
                                    }
                                    bestValue={quote.available && quote.partner === bestPpwPartner}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            className="pci-lpr__partner-tabs"
                            role="tablist"
                            aria-label="Finance partner"
                        >
                            {GRID_PARTNERS.map((id) => (
                                <button
                                    key={id}
                                    type="button"
                                    role="tab"
                                    className={`pci-lpr__partner-tab pci-lpr__partner-tab--${partnerModifier(
                                        id,
                                    )}${gridPartner === id ? ' pci-lpr__partner-tab--on' : ''}`}
                                    aria-selected={gridPartner === id}
                                    onClick={() => setGridPartner(id)}
                                >
                                    <span className="pci-lpr__partner-dot" aria-hidden />
                                    {id}
                                </button>
                            ))}
                        </div>

                        {!matrix || matrix.rates.length === 0 || matrix.escalators.length === 0 ? (
                            <p className="pci-lpr__note pci-lpr__note--warn" role="status">
                                <WarningCircle size={15} weight="fill" aria-hidden />
                                {noPricingMessage}
                            </p>
                        ) : (
                            <>
                                <div className="pci-lpr__matrix-wrap pci-live-pricing__matrix-wrap">
                                    <table className="pci-lpr__matrix">
                                        <caption className="pci-sr-only">
                                            {gridPartner} pricing by rate and escalator
                                        </caption>
                                        <thead>
                                            <tr>
                                                <th scope="col">Rate</th>
                                                {matrix.escalators.map((esc) => (
                                                    <th key={esc} scope="col">
                                                        {formatEscalatorPercent(esc)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {matrix.rates.map((rate, rateIdx) => {
                                                const isTargetRate =
                                                    featuredRate > 0 &&
                                                    nearlyEqual(rate, featuredRate);
                                                return (
                                                    <tr
                                                        key={rate}
                                                        className={
                                                            isTargetRate
                                                                ? 'pci-lpr__matrix-row--allin'
                                                                : undefined
                                                        }
                                                    >
                                                        <th scope="row">
                                                            {formatPpaRate(rate)}
                                                            {isTargetRate ? (
                                                                <em className="pci-lpr__matrix-allin">
                                                                    target/min
                                                                </em>
                                                            ) : null}
                                                        </th>
                                                        {matrix.cells[rateIdx].map((cell) => {
                                                            const key = matrixCellKey(cell);
                                                            const selected =
                                                                nearlyEqual(
                                                                    cell.rate,
                                                                    result.rate,
                                                                ) &&
                                                                nearlyEqual(
                                                                    cell.escalator,
                                                                    result.escalator,
                                                                    1e-4,
                                                                );
                                                            const best = key === bestMatrixKey;
                                                            return (
                                                                <td key={key}>
                                                                    <button
                                                                        type="button"
                                                                        className={`pci-lpr__matrix-cell${
                                                                            selected
                                                                                ? ' pci-lpr__matrix-cell--on'
                                                                                : ''
                                                                        }${
                                                                            best
                                                                                ? ' pci-lpr__matrix-cell--best'
                                                                                : ''
                                                                        }${
                                                                            !cell.available
                                                                                ? ' pci-lpr__matrix-cell--off'
                                                                                : ''
                                                                        }`}
                                                                        disabled={!cell.available}
                                                                        aria-pressed={selected}
                                                                        title={
                                                                            cell.available
                                                                                ? `Y1 ${formatMoney(cell.estimatedMonthly, 0)} · PPW $${cell.ppw?.toFixed(2)} · EPC ${formatMoney(cell.epc, 0)}`
                                                                                : 'Unavailable'
                                                                        }
                                                                        onClick={() =>
                                                                            selectCell(cell)
                                                                        }
                                                                    >
                                                                        <span className="pci-lpr__matrix-cell-line pci-lpr__matrix-cell-line--lead">
                                                                            <em>Y1</em>
                                                                            <strong>
                                                                                {formatMoney(
                                                                                    cell.estimatedMonthly,
                                                                                    0,
                                                                                )}
                                                                            </strong>
                                                                        </span>
                                                                        <span className="pci-lpr__matrix-cell-line">
                                                                            <em>PPW</em>
                                                                            <strong>
                                                                                {cell.ppw != null
                                                                                    ? `$${cell.ppw.toFixed(2)}`
                                                                                    : '—'}
                                                                            </strong>
                                                                        </span>
                                                                        <span className="pci-lpr__matrix-cell-line">
                                                                            <em>EPC</em>
                                                                            <strong>
                                                                                {formatMoney(
                                                                                    cell.epc,
                                                                                    0,
                                                                                )}
                                                                            </strong>
                                                                        </span>
                                                                    </button>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <p className="pci-lpr__legend">
                                    <span className="pci-lpr__legend-key">
                                        <span className="pci-lpr__legend-swatch pci-lpr__legend-swatch--on" />
                                        Selected
                                    </span>
                                    <span className="pci-lpr__legend-key">
                                        <span className="pci-lpr__legend-swatch pci-lpr__legend-swatch--best" />
                                        Lowest PPW
                                    </span>
                                    <span className="pci-lpr__legend-key">
                                        <span className="pci-lpr__legend-swatch" />
                                        Click to apply
                                    </span>
                                </p>
                            </>
                        )}
                    </>
                )}

                <p className="pci-lpr__note" role="note">
                    <Info size={15} weight="fill" aria-hidden />
                    <span>
                        Y1 monthly ≈ rate × production ÷ 12.{' '}
                        {isLivePricing
                            ? 'PPW is Expected_PPW from PPWDetails for that rate and escalator, at the multiplier band closest to this design.'
                            : 'PPW = slope × multiplier + intercept from the Excel row for that rate and escalator.'}{' '}
                        Y1 stays flat across escalators while PPW and EPC move.
                    </span>
                </p>
            </div>
        </section>
    );
};

export default PreContractLivePricingRail;
